import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import {
  MAX_MAP_AUTHOR_LENGTH,
  MAX_MAP_DESCRIPTION_LENGTH,
  MAX_MAP_NOTES_LENGTH,
  MAX_MAP_REVISION_LENGTH,
  type MapMeta,
} from '../../sim/map/index.ts';

export function MapMetaFields({
  value,
  onChange,
  commit = 'blur',
}: {
  value: MapMeta;
  onChange: (patch: Partial<MapMeta>) => void;
  commit?: 'blur' | 'input';
}): JSX.Element {
  return (
    <div class="editor-map-meta">
      <MetaLine
        label="Author"
        value={value.author}
        maxLength={MAX_MAP_AUTHOR_LENGTH}
        commit={commit}
        onChange={(author) => onChange({ author })}
      />
      <MetaLine
        label="Version"
        value={value.revision}
        maxLength={MAX_MAP_REVISION_LENGTH}
        hint="Map release, not the file format."
        commit={commit}
        onChange={(revision) => onChange({ revision })}
      />
      <MetaLine
        label="Description"
        value={value.description}
        maxLength={MAX_MAP_DESCRIPTION_LENGTH}
        commit={commit}
        onChange={(description) => onChange({ description })}
      />
      <MetaLine
        label="Notes"
        value={value.notes}
        maxLength={MAX_MAP_NOTES_LENGTH}
        multiline
        commit={commit}
        onChange={(notes) => onChange({ notes })}
      />
    </div>
  );
}

function MetaLine({
  label,
  value,
  maxLength,
  multiline = false,
  hint,
  commit,
  onChange,
}: {
  label: string;
  value: string;
  maxLength: number;
  multiline?: boolean;
  hint?: string;
  commit: 'blur' | 'input';
  onChange: (next: string) => void;
}): JSX.Element {
  const field = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const element = field.current;

    if (element && document.activeElement !== element) {
      element.value = value;
    }
  }, [value]);

  const attach = (node: HTMLInputElement | HTMLTextAreaElement | null): void => {
    field.current = node;
  };

  const readValue = (): string => field.current?.value ?? '';

  const emit = (next: string): void => {
    if (next !== value) {
      onChange(next);
    }
  };

  const onKeyDown = (
    event: JSX.TargetedKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    if (event.key !== 'Enter' || multiline || commit !== 'blur') {
      return;
    }

    event.preventDefault();
    field.current?.blur();
  };

  const shared = {
    class: 'ui-text-input',
    'aria-label': label,
    'aria-description': hint,
    maxlength: maxLength,
    spellcheck: false as const,
    defaultValue: value,
    onInput: (event: Event) => {
      if (commit === 'input') {
        emit((event.currentTarget as HTMLInputElement | HTMLTextAreaElement).value);
      }
    },
    onBlur: () => {
      emit(readValue());
    },
    onKeyDown,
  };

  return (
    <div
      class={`ui-control ui-control-wide${multiline ? ' ui-control-stack' : ''}`}
      title={hint}
    >
      <span class="ui-control-label">{label}</span>
      {multiline ? (
        <textarea {...shared} ref={attach} rows={3} />
      ) : (
        <input {...shared} ref={attach} type="text" />
      )}
    </div>
  );
}
